import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Form, Input, Select, Typography } from 'antd';
import type { FormInstance, FormListFieldData } from 'antd';
import { Trash2, ChevronDown, Plus } from 'lucide-react';
import { HtsInputType } from '@evaltick/shared';
import { cn } from '@/utils';
import { DragHandle } from '@/components/DragHandle';
import { useDragReorder, type DragItemProps } from '@/hooks';
import { GROUP_SUB_FIELD_TYPE_OPTIONS, NEEDS_OPTIONS, isGroupFieldValid } from '../htsQuestionFormOptions';
import { ANSWER_TYPE_CARDS } from '../htsQuestionWizardConfig';
import { SelectableOption } from '../SelectableOption';
import { StepHeader } from '../StepHeader';
import type { HtsGroupFieldFormValue, HtsQuestionFormValues } from '../htsQuestionFormTypes';
import styles from '../HtsQuestionForm.module.scss';

const PANEL_TRANSITION = { duration: 0.2, ease: 'easeInOut' } as const;

interface AnswerTypeGridProps {
  value?: HtsInputType;
  onChange?: (value: HtsInputType) => void;
  onSelect?: () => void;
}

const AnswerTypeGrid: React.FC<AnswerTypeGridProps> = ({ value, onChange, onSelect }) => (
  <div className={styles.answerTypeGrid} role="radiogroup" aria-label="Answer type">
    {ANSWER_TYPE_CARDS.map((card) => (
      <SelectableOption
        key={card.value}
        variant="card"
        selected={value === card.value}
        onClick={() => {
          onChange?.(card.value);
          onSelect?.();
        }}
      >
        <div className={styles.answerTypeIcon}>{card.icon}</div>
        <div className={styles.answerTypeLabel}>{card.label}</div>
        <div className={styles.answerTypeHint}>{card.hint}</div>
      </SelectableOption>
    ))}
  </div>
);

interface GroupFieldOptionsPanelProps {
  form: FormInstance<HtsQuestionFormValues>;
  fieldName: number;
  typeLabel: string;
  expanded: boolean;
  onToggleExpanded: () => void;
}

const GroupFieldOptionsPanel: React.FC<GroupFieldOptionsPanelProps> = ({
  form,
  fieldName,
  typeLabel,
  expanded,
  onToggleExpanded,
}) => {
  const [draft, setDraft] = React.useState('');
  const options: string[] = Form.useWatch(['groupFields', fieldName, 'options'], { form, preserve: true }) ?? [];
  const reorder = useDragReorder(options.length);

  const getLiveOptions = (): string[] => form.getFieldValue(['groupFields', fieldName, 'options']) ?? [];

  const setOptions = (next: string[]) => {
    form.setFieldValue(['groupFields', fieldName, 'options'], next);
  };

  // Reads the form's live value rather than the `options` watch above —
  // rapid consecutive calls (e.g. holding Enter) can fire before React
  // re-renders with the previous update's watched value, so closing over
  // `options` here would silently drop entries.
  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setOptions([...getLiveOptions(), trimmed]);
    setDraft('');
  };

  const handleRemove = (index: number) => {
    setOptions(getLiveOptions().filter((_, i) => i !== index));
  };

  const handleReorder = (from: number, to: number) => {
    const next = [...getLiveOptions()];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOptions(next);
  };

  return (
    <div className={styles.groupFieldOptions} ref={reorder.containerRef} {...reorder.getContainerProps(handleReorder)}>
      <button type="button" className={styles.optionsPanelHeader} onClick={onToggleExpanded}>
        <span className={styles.optionsPanelHeaderLabel}>
          {typeLabel} ({options.length})
        </span>
        <ChevronDown
          className={cn(styles.optionsPanelChevron, !expanded && styles.optionsPanelChevronCollapsed)}
          style={{ width: '1em', height: '1em' }}
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={PANEL_TRANSITION}
            className={styles.optionsPanelBody}
          >
            {options.length === 0 ? (
              <div className={styles.optionsPanelEmpty}>No options yet</div>
            ) : (
              <div className={styles.optionsPanelList}>
                {options.map((option, index) => {
                  const isDragging = reorder.isDragging(index);
                  const isDragOver = reorder.isDragOver(index) && !isDragging;
                  return (
                    <div
                      key={option}
                      className={cn(
                        styles.optionsPanelRow,
                        isDragging && styles.optionsPanelRowDragging,
                        isDragOver && styles.optionsPanelRowDragOver
                      )}
                      {...reorder.getItemProps(index)}
                    >
                      <DragHandle />
                      <span className={styles.optionsPanelText}>{option}</span>
                      <Button
                        type="text"
                        size="small"
                        icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
                        onClick={() => handleRemove(index)}
                        aria-label={`Remove ${option}`}
                        danger
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div className={styles.optionsPanelAddRow}>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleAdd();
                }}
                placeholder="Type an option and press Enter"
              />
              <Button
                type="default"
                size="small"
                icon={<Plus style={{ width: '1em', height: '1em' }} />}
                className={cn(styles.btnSecondary, styles.btnSecondarySmall)}
                onClick={handleAdd}
              >
                Add
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface GroupFieldRowProps {
  form: FormInstance<HtsQuestionFormValues>;
  field: FormListFieldData;
  dragItemProps: DragItemProps;
  isDragging: boolean;
  isDragOver: boolean;
  isLast: boolean;
  onRemove: (name: number) => void;
  onAddRow: () => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onExpand: () => void;
}

const GroupFieldRow: React.FC<GroupFieldRowProps> = ({
  form,
  field,
  dragItemProps,
  isDragging,
  isDragOver,
  isLast,
  onRemove,
  onAddRow,
  isExpanded,
  onToggleExpanded,
  onExpand,
}) => {
  const rowType = Form.useWatch(['groupFields', field.name, 'inputType'], { form, preserve: true });
  const needsRowOptions = rowType != null && NEEDS_OPTIONS.has(rowType);

  return (
    <div
      className={cn(
        styles.groupFieldRow,
        isDragging && styles.groupFieldRowDragging,
        isDragOver && styles.groupFieldRowDragOver
      )}
      {...dragItemProps}
    >
      <Form.Item {...field} name={[field.name, 'key']} noStyle>
        <Input type="hidden" />
      </Form.Item>
      <div className={styles.groupFieldRowMain}>
        <DragHandle />
        <Form.Item
          {...field}
          name={[field.name, 'label']}
          rules={[{ required: true, message: 'A field label is required' }]}
          noStyle
        >
          <Input
            placeholder="Label shown to the candidate"
            className={styles.groupFieldLabel}
            autoFocus={isLast}
            onPressEnter={(e) => {
              e.preventDefault();
              onAddRow();
            }}
          />
        </Form.Item>
        <Form.Item
          {...field}
          name={[field.name, 'inputType']}
          rules={[{ required: true, message: 'Choose a sub-field type' }]}
          noStyle
        >
          <Select
            placeholder="Type"
            options={GROUP_SUB_FIELD_TYPE_OPTIONS}
            className={styles.groupFieldType}
            popupMatchSelectWidth={false}
            onChange={(value: HtsInputType) => {
              if (NEEDS_OPTIONS.has(value)) onExpand();
            }}
          />
        </Form.Item>
        <Button icon={<Trash2 style={{ width: '1em', height: '1em' }} />} onClick={() => onRemove(field.name)} type="text" danger />
      </div>

      <AnimatePresence initial={false}>
        {needsRowOptions && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={PANEL_TRANSITION}
            className={styles.groupFieldOptionsWrapper}
          >
            <GroupFieldOptionsPanel
              form={form}
              fieldName={field.name}
              typeLabel={rowType === HtsInputType.SELECT ? 'DROPDOWN CHOICES' : 'CHECKBOX CHOICES'}
              expanded={isExpanded}
              onToggleExpanded={onToggleExpanded}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface Step3AnswerTypeProps {
  form: FormInstance<HtsQuestionFormValues>;
  inputType: HtsInputType | undefined;
}

export const Step3AnswerType: React.FC<Step3AnswerTypeProps> = ({ form, inputType }) => {
  const [changingType, setChangingType] = React.useState(false);
  const [collapsedKeys, setCollapsedKeys] = React.useState<Set<number>>(new Set());
  const groupFieldsCount = Form.useWatch('groupFields', { form, preserve: true })?.length ?? 0;
  const rowReorder = useDragReorder(groupFieldsCount);

  const typeLabel = ANSWER_TYPE_CARDS.find((c) => c.value === inputType)?.label.toLowerCase();
  const needsSubStep = inputType != null && (NEEDS_OPTIONS.has(inputType) || inputType === HtsInputType.REPEATABLE_GROUP);
  const showGrid = !needsSubStep || changingType;

  const toggleExpanded = (key: number) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const expandFor = (key: number) => {
    setCollapsedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  return (
    <div>
      <StepHeader
        stepNumber={3}
        title="How will the candidate answer?"
        description={
          showGrid
            ? 'Pick the input control. This decides how the answer is captured — not what happens to it afterward.'
            : `Set up the choices/rows for this "${typeLabel}" question.`
        }
      />

      <AnimatePresence mode="wait">
        {showGrid ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={PANEL_TRANSITION}
          >
            <Form.Item name="inputType" rules={[{ required: true, message: 'Choose an answer type' }]}>
              <AnswerTypeGrid onSelect={() => setChangingType(false)} />
            </Form.Item>
          </motion.div>
        ) : (
          <motion.div
            key="editor"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={PANEL_TRANSITION}
          >
            <button type="button" className={styles.backToTypesLink} onClick={() => setChangingType(true)}>
              Change answer type
            </button>

            {inputType && NEEDS_OPTIONS.has(inputType) && (
            <Form.List
              name="options"
              rules={[
                {
                  validator: async (_, options: string[] | undefined) => {
                    if (!options || options.filter((o) => o?.trim()).length < 1) {
                      return Promise.reject(new Error('Add at least one choice'));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <div className={styles.optionsList}>
                  <div className={styles.optionsHeaderRow}>
                    <label className={styles.optionsLabel}>Choices *</label>
                    <Button
                      type="default"
                      size="small"
                      icon={<Plus style={{ width: '1em', height: '1em' }} />}
                      className={cn(styles.btnSecondary, styles.btnSecondarySmall)}
                      onClick={() => add('')}
                    >
                      Add choice
                    </Button>
                  </div>
                  {fields.length === 0 && (
                    <div className={styles.optionsPanelEmpty}>Add at least one choice</div>
                  )}
                  {fields.map((field, index) => (
                    <div key={field.key} className={styles.optionRow}>
                      <Form.Item
                        {...field}
                        rules={[{ required: true, message: 'Choice text is required' }]}
                        noStyle
                      >
                        <Input
                          placeholder="Choice text"
                          autoFocus={index === fields.length - 1}
                          onPressEnter={(e) => {
                            e.preventDefault();
                            add('');
                          }}
                        />
                      </Form.Item>
                      <Button
                        icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
                        onClick={() => remove(field.name)}
                        type="text"
                        danger
                      />
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          )}

          {inputType === HtsInputType.REPEATABLE_GROUP && (
            <Form.List
              name="groupFields"
              rules={[
                {
                  validator: async (_, fields: HtsGroupFieldFormValue[] | undefined) => {
                    if (!fields || fields.length === 0) {
                      return Promise.reject(new Error('Add at least one row field'));
                    }
                    if (!fields.every(isGroupFieldValid)) {
                      return Promise.reject(
                        new Error("Each row field needs a label, and at least one option if it's a Dropdown or Checkboxes")
                      );
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove, move }) => {
                const addRow = () => add({ inputType: HtsInputType.TEXT });
                return (
                  <div
                    className={styles.optionsList}
                    ref={rowReorder.containerRef}
                    {...rowReorder.getContainerProps(move)}
                  >
                    <div className={styles.optionsHeaderRow}>
                      <label className={styles.optionsLabel}>Row fields *</label>
                      <Button
                        type="default"
                        size="small"
                        icon={<Plus style={{ width: '1em', height: '1em' }} />}
                        className={cn(styles.btnSecondary, styles.btnSecondarySmall)}
                        onClick={addRow}
                      >
                        Add Row
                      </Button>
                    </div>
                    <Typography.Paragraph className={styles.repeatHint}>
                      Each row the candidate adds (e.g. one employer, one education entry) has these columns. Choose
                      Dropdown or Checkboxes to add its options right here — no separate step.
                    </Typography.Paragraph>
                    {fields.map((field, index) => (
                      <GroupFieldRow
                        key={field.key}
                        form={form}
                        field={field}
                        dragItemProps={rowReorder.getItemProps(index)}
                        isDragging={rowReorder.isDragging(index)}
                        isDragOver={rowReorder.isDragOver(index) && !rowReorder.isDragging(index)}
                        isLast={index === fields.length - 1}
                        onRemove={remove}
                        onAddRow={addRow}
                        isExpanded={!collapsedKeys.has(field.key)}
                        onToggleExpanded={() => toggleExpanded(field.key)}
                        onExpand={() => expandFor(field.key)}
                      />
                    ))}
                  </div>
                );
              }}
            </Form.List>
          )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Step3AnswerType;
